/*
 * Copyright (c) 2022-2022. Revo Digital
 * ---
 * Author: gabrielecavallo
 * File: ShapesFeature.tsx
 * Project: mle-client-n
 * Committed last: 2022/11/25 @ 938
 * ---
 * Description:
 */

import {EditorFeature} from '../../../common/EditorFeature';
import {Editor} from '../../../../observables/Editor';
import {ShapeTypes} from '../../../../../common/enums';
import Pamela from '@revodigital/pamela';
import {constructObservableShape} from '../../../../observables';
import {ObservableShape} from '../../../../observables/ObservableShape';
import {MleToaster, Toasts} from '../../../../../common/toast';
import {ImageSourcePolicy} from '@revodigital/pamela/lib/shapes/Image';
import {action} from 'mobx';
import {AREA_LAYER} from '../../../work-area/DrawingAreaFeature';
import {pamelaLayerToObservables} from '../../../../../common/pamela-utils';
import {DataRefType, DynamicDataRef} from '@revodigital/mpes';
import {DefaultShapesOptions, ShapeFactory} from "../factory/shape/ShapeFactory";
import {defaultConfig, shapeConfig} from "../../../../../config";

export const SHAPE_FNAME = 'shapes';

/**
 * Parametri da specificare quando si aggiunge una nuova immagine
 */
export interface AddImageParams {
    dataurl: string;
    src: string;
    assetId: number;
}

export class ShapesFeature extends EditorFeature<void> {
    constructor(parent: Editor) {
        super(SHAPE_FNAME, parent);
        //makeObservable(this);
    }

    init(): boolean {
        return false;
    }

    /**
     * Crea un'immagine a partire dallo url
     * @param options Opzioni per la creazione della nuova immagine
     */
    public createImageFromDataURL(options: AddImageParams): Pamela.Image {
        return new Pamela.Image({
            sourceData: options.dataurl,
            sourcePolicy: ImageSourcePolicy.DATAURL,
            src: options.src,
            assetId: options.assetId,
        });
    }

    /**
     * Aggiunge una tabella mylabel con i campi già collegati
     */
    @action
    public async addMyLabelTable(companyId: number) {
        const shapesFactory = new ShapeFactory();

        const newShape = shapesFactory.produceMyLabelTable();
        const shapeType = ShapeTypes.TABLE;
        const projDT = await this.sourcePointer.dataOriginFeature.getProjectDataOrigin(
            companyId);

        // Collego le origini dati mancanti
        const allKeys: string[] = shapeConfig.myLabelTable.dataOrigins.map((it: any) => it.key);

        for (const key of allKeys) {
            if (key !== "" && key.length > 0) {
                const fieldName = key.replaceAll('{', '').replaceAll('}', '');
                // Controllo se la dataref è presente
                if (!this.sourcePointer.dataOriginFeature.hasDataRef(DataRefType.DYNAMIC,
                    fieldName)) {
                    // Aggiungo se è mancante
                    this.sourcePointer.dataOriginFeature.addDataRef(new DynamicDataRef(
                        projDT?.id.toString() as string,
                        fieldName));
                }
            }
        }

        // Inizializza la forma applicando posizione nome e id
        this.initShape(newShape, shapeType);
        this.sourcePointer.pamelaLayer__nonObservable.add(newShape);
        this.sourcePointer.shapes.replace([...this.sourcePointer.shapes, constructObservableShape(
            newShape,
            this.sourcePointer) as ObservableShape<any>]);

        setTimeout(() => {
            this.sourcePointer.select(newShape);
        }, 200);

        // TODO: Tutti gli "scatti" andrebbero implementati in un Proxy scorporandoli così dall'Editor e le sue componenti
        this.sourcePointer.history.takeSnapshot();
    }

    public async addIngredientsList(companyId: number) {
        // Creo la factory
        const factory = new ShapeFactory();
        // Ottengo l'origine dati su cui lavorare
        const projDT = await this.sourcePointer.dataOriginFeature.getProjectDataOrigin(
            companyId);
        // Creo la forma
        const newShape = factory.produceIngredientsList();

        // Aggiungo le origini dati mancanti
        const fieldName = defaultConfig.dataOrigins.ingredientsList;
        if (!this.sourcePointer.dataOriginFeature.hasDataRef(DataRefType.DYNAMIC,
            fieldName)) {
            this.sourcePointer.dataOriginFeature.addDataRef(new DynamicDataRef(
                projDT?.id.toString() as string,
                fieldName));
        }

        this.initShape(newShape, ShapeTypes.RICH_TEXT);
        this.sourcePointer.pamelaLayer__nonObservable.add(newShape);
        this.sourcePointer.shapes.replace([...this.sourcePointer.shapes, constructObservableShape(
            newShape,
            this.sourcePointer) as ObservableShape<any>]);

        setTimeout(() => {
            this.sourcePointer.select(newShape);
        }, 200);

        // TODO: Tutti gli "scatti" andrebbero implementati in un Proxy scorporandoli così dall'Editor e le sue componenti
        this.sourcePointer.history.takeSnapshot();
    }

    /**
     * Adds a new shape to the work area
     * @param shapeType
     * @param options
     */
    public async createShape<OptionsType>(shapeType: ShapeTypes, options?: OptionsType) {
        let newShape: Pamela.Shape;

        if (shapeType !== ShapeTypes.IMAGE)
            newShape = await this.createNewShape<Pamela.Shape>(shapeType) as Pamela.Shape;
        else newShape = this.createImageFromDataURL(options as any as AddImageParams);

        if (newShape)
            this.addShape(newShape, shapeType);
    }

    /**
     * Aggiunge una forma all'area di lavoro
     * @param shape
     * @param shapeType
     */
    public addShape(shape: Pamela.Shape, shapeType: ShapeTypes) {
        // Inizializza la forma applicando posizione nome e id
        this.initShape(shape, shapeType);
        this.sourcePointer.pamelaLayer__nonObservable.add(shape);
        this.sourcePointer.shapes.replace([...this.sourcePointer.shapes, constructObservableShape(
            shape,
            this.sourcePointer) as ObservableShape<any>]);

        setTimeout(() => {
            this.sourcePointer.select(shape);
        }, 200);

        // TODO: Tutti gli "scatti" andrebbero implementati in un Proxy scorporandoli così dall'Editor e le sue componenti
        this.sourcePointer.history.takeSnapshot();
    }

    /**
     * Popola le forme a partire dai layer che le contengono
     * @param layers
     * @param options
     */
    @action
    public populateShapesFromLayers(layers: Pamela.Layer[], options: { fromSnapshot: boolean } = {fromSnapshot: false}) {
        if (options.fromSnapshot) {
            this.sourcePointer.pamelaLayer__nonObservable.children = [];
            this.sourcePointer.shapes.replace([]);
        }

        layers.forEach(layer => {
            if (layer.name() === AREA_LAYER) return;

            pamelaLayerToObservables(layer, this.sourcePointer).forEach((shape) => {
                this.sourcePointer.pamelaLayer__nonObservable.add(shape.pamelaShape__nonObservable);
                this.sourcePointer.shapes.push(shape);
            });
        });
    }

    /**
     * Rimuove una forma permanentemente
     * @param shapeToDelete
     */
    public deleteShape(shapeToDelete: ObservableShape) {
        shapeToDelete.pamelaShape__nonObservable.destroy();
        this.sourcePointer.shapes.splice(0,
            this.sourcePointer.shapes.length,
            ...this.sourcePointer.shapes.filter(shape => {
                return shape.pamelaShape__nonObservable.id() !== shapeToDelete.pamelaShape__nonObservable.id();
            }));
        this.sourcePointer.deselect();
    }

    /**
     * Inizializza una nuova forma
     * @param newShape
     * @param shapeType
     */
    public initShape(newShape: Pamela.Shape, shapeType: ShapeTypes) {
        const scale = this.sourcePointer.pamelaStage__nonObservable.scaleX();
        newShape.id(this.sourcePointer.generateShapeId(shapeType));
        newShape.name(shapeType);
        newShape.x((this.sourcePointer.pamelaStage__nonObservable.absolutePosition().x * -1 + 80) / scale);
        newShape.y((this.sourcePointer.pamelaStage__nonObservable.absolutePosition().y * -1 + 80) / scale);
        newShape.draggable(true);
    }

    /**
     * Restituisce la prima forma nell'etichetta con l'id passato o undefined se non esiste
     * @param id
     */
    public getShapeById(id: string): Pamela.Shape {
        return this.sourcePointer.pamelaLayer__nonObservable.find(`#${id}`)[0] as Pamela.Shape;
    }

    /**
     * Controlla se quest'etichetta contiene una certa forma
     * @param shape
     */
    public contains(shape: Pamela.Shape): boolean {
        return !!this.getShapeById(shape.id());
    }

    /**
     * Crea una nuova forma
     * @param shapeType Tipo di forma
     * @param options Opzioni aggiuntive
     */
    public async createNewShape<Shape extends Pamela.Shape, Options = void>(shapeType: ShapeTypes, options?: Options): Promise<Shape | undefined> {
        const shapeFactory = new ShapeFactory();

        // TODO: Evitare di dover passare per unknown
        switch (shapeType) {
            case ShapeTypes.RECT:
                return shapeFactory.produceRectangle() as unknown as Shape;

            case ShapeTypes.CIRCLE:
                return shapeFactory.produceCircle() as unknown as Shape;

            case ShapeTypes.TRIANGLE:
                return shapeFactory.produceTriangle() as unknown as Shape;

            case ShapeTypes.ELLIPSE:
                return shapeFactory.produceEllipse() as unknown as Shape;

            case ShapeTypes.WEDGE:
                return shapeFactory.produceWedge() as unknown as Shape;

            case ShapeTypes.BARCODE:
                return shapeFactory.produceBarcode() as unknown as Shape;

            case ShapeTypes.EXPORT_VARIABLE:
                return shapeFactory.produceExportVariable() as unknown as Shape;

            case ShapeTypes.TEXT:
                const shape = shapeFactory.produceSimpleText() as unknown as Shape;
                shape.fill('black');
                return shape;

            case ShapeTypes.RICH_TEXT:
                return new Pamela.RichText({
                    ...DefaultShapesOptions.RICH_TEXT,
                    ...options
                }) as unknown as Shape;

            case ShapeTypes.TABLE:
                return shapeFactory.produceTable() as unknown as Shape;

            default:
                MleToaster.getInstance().show(
                    Toasts.INVALID_NEW_OBJECT(shapeType)
                );
                return undefined;
        }
    }


    destroy(): boolean {
        return true;
    }
}